import { useState, useEffect } from 'react';
import { Box, Flex, Text, Card, AlertDialog, VisuallyHidden } from '@radix-ui/themes';
import { useSelector } from 'react-redux';
import { CheckIcon, DotFilledIcon, StarFilledIcon, QuestionMarkCircledIcon, ClockIcon, ArrowRightIcon } from '@radix-ui/react-icons';
import StudentQuiz from './StudentQuiz';
import { DateTime, Info } from 'luxon';
import { useDispatch } from 'react-redux';
import { updateStudentQuiz } from '../../redux/slices/studentAccountSlice';
import './StudentFeedBrowser.css';
import { motion } from 'framer-motion';
import MessageBox from '../common/MessageBox';

const StudentFeedBrowser = ({ onQuizSelect, quizzesSummary: quizzes, onQuizComplete }) => {
    // const quizzes = useSelector((state) => state.studentAccount.quizzes);
    const [weekDays, setWeekDays] = useState([]);
    const [selectedDay, setSelectedDay] = useState(null);
    const [filteredQuizzes, setFilteredQuizzes] = useState([]);
    const [selectedQuiz, setSelectedQuiz] = useState(null);
    const dispatch = useDispatch();
    console.log('inside feed browser>>>>>>>>>>>>>>>>', quizzes);

   
    if(quizzes['error'] != null){
        return <Text>{quizzes['error']}</Text>
    }

    useEffect(() => {
        // Get the current date and calculate the week's days
        const today = DateTime.now();
        const days = [];
        for (let i = 6; i >= 0; i--) {
            const day = today.minus({ days: i });
            days.push(day);
        }
        setWeekDays(days);
        setSelectedDay(today.weekdayShort);
    }, []);

    useEffect(() => {
        if (selectedDay) {
            console.log('quizzes fetched:', quizzes);
            const dayQuizzes = quizzes.filter(quiz =>
                DateTime.fromISO(quiz.created_at).weekdayShort === selectedDay
            );
            setFilteredQuizzes(dayQuizzes);
        }
    }, [selectedDay, quizzes]);

    const getDayStatus = (day) => {
        const dayQuizzes = quizzes.filter(quiz =>
            DateTime.fromISO(quiz.created_at).hasSame(day, 'day')
        );

        if (!dayQuizzes || dayQuizzes.length === 0) return 'empty';

        const attemptedQuizzes = dayQuizzes.filter(quiz => quiz.quiz_attempts?.length > 0);
        const perfectQuizzes = dayQuizzes.filter(quiz => {
            const lastAttempt = quiz.quiz_attempts[quiz.quiz_attempts.length - 1];
            return lastAttempt && lastAttempt.percentage === 100;
        });

        if (attemptedQuizzes.length === dayQuizzes.length) {
            return perfectQuizzes.length === dayQuizzes.length ? 'perfect' : 'completed';
        }
        return 'incomplete';
    };

    const getUnattemptedCount = (day) => {
        const dayQuizzes = quizzes.filter(quiz =>
            DateTime.fromISO(quiz.created_at).hasSame(day, 'day')
        );
        return dayQuizzes.filter(quiz => quiz.quiz_attempts?.length === 0).length;
    };

    const renderDayIndicator = (day) => {
        const status = getDayStatus(day);

        if (status === 'empty') {
            return <DotFilledIcon className="empty-day" />;
        } else if (status === 'perfect') {
            return (
                <motion.span
                    initial={{ scale: 4, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1, ease: "easeOut", delay: 0.5 }}
                >
                    <StarFilledIcon className="perfect-score" />
                </motion.span>
            );
        } else if (status === 'completed') {
            return <CheckIcon className="completed-score" />;
        }
        return <Text size="1" weight="bold" className="incomplete-score">{getUnattemptedCount(day)}</Text>;
    };

    const renderQuizItem = (quiz) => {
        const hasAttempts = quiz.quiz_attempts?.length > 0;
        const lastAttempt = hasAttempts ? quiz.quiz_attempts[quiz.quiz_attempts.length - 1] : null;
        const isPerfect = hasAttempts && lastAttempt?.percentage === 100;

        return (
            <Card
                key={quiz.id}
                className="quiz-item"
                onClick={() => {
                    setSelectedQuiz(quiz);
                    onQuizSelect(quiz);
                }}
            >
                <Flex justify="between" align="center" gap="4">
                    <Box>
                        <Text size="2" weight="bold">{quiz.lesson.title}</Text>
                        <Text size="1" color="gray"></Text>
                        <Flex align="center" gap="1">
                            <QuestionMarkCircledIcon />
                            <Text size="1" color="gray">
                                {quiz.questions_count} questions
                            </Text>
                            <Flex align="center" gap="1">
                                <ClockIcon />
                                <Text size="1" color="gray">
                                    {DateTime.fromISO(quiz.created_at).toRelative()}
                                </Text>
                            </Flex>
                        </Flex>
                    </Box>
                    <Box>
                        {hasAttempts ? (
                            isPerfect ? (
                                <StarFilledIcon width="20" height="20" className="perfect-score" />
                            ) : (
                                <CheckIcon width="20" height="20" color='green' className="completed-score" />
                            )
                        ) : (
                            <Text size="2" className="incomplete-score"><ArrowRightIcon /></Text>
                        )}
                    </Box>
                </Flex>

            </Card>
        );
    };

    const handleQuizComplete = async (quizAttempt) => {
        // Find the quiz and create a new copy with the updated attempts
        console.log('quizAttempt:', quizAttempt);
        const updatedQuiz = quizzes.find(quiz => quiz.id === quizAttempt.quiz_id);
        if (!updatedQuiz) return;
        
        const newQuiz = {
            ...updatedQuiz,
            quiz_attempts: updatedQuiz.quiz_attempts
            ? [...updatedQuiz.quiz_attempts, quizAttempt]
            : [quizAttempt]
        };
        
        setSelectedQuiz(null);
        // Dispatch the update action
        dispatch(updateStudentQuiz(newQuiz));
    };

    const renderCurrentSummary = () => {
        const totalQuizzes = quizzes.length;
        const attemptedQuizzes = quizzes.filter(quiz => quiz.quiz_attempts && quiz.quiz_attempts.length > 0).length;

        // Calculate average score from first attempts only
        const averageScore = quizzes.reduce((sum, quiz) => {
            if (!quiz.quiz_attempts || quiz.quiz_attempts.length === 0) return sum;
            const firstAttempt = quiz.quiz_attempts[0]; // Get first attempt
            return sum + firstAttempt.percentage;
        }, 0) / (attemptedQuizzes || 1); // Avoid division by zero

        return (
            <Box mb="4">
                <Flex justify="between" align="center" mx="auto" pb="2" style={{ maxWidth: '260px', borderBottom: '1px solid var(--gray-9)' }}>
                    <Text size="1" style={{ color: 'var(--accent-1)' }}>
                        Score: {averageScore.toFixed(1)}%
                    </Text>
                    <Text size="1" style={{ color: 'var(--accent-1)' }}>
                        {attemptedQuizzes}/{totalQuizzes} quizzes
                    </Text>
                </Flex>
            </Box>
        );
    }



    if (quizzes['error']) return (
        <MessageBox type="warning" title="No Subscriptions" message={quizzes['error']} />
    );


    return (
        <Box>
            <Box className="feed-browser" p="4" mb="4">
                {renderCurrentSummary()}
                <Flex gap="2" justify="center">
                    {weekDays.map((day) => (
                        <Box
                            key={day.toISO()}
                            onClick={() => setSelectedDay(day.weekdayShort)}
                            className={`day-indicator ${selectedDay === day.weekdayShort ? 'selected' : ''}`}
                        >
                            <Flex direction="column" align="center" gap="1">
                                <Box className="day-indicator-icon-box">
                                    {renderDayIndicator(day)}
                                </Box>
                                <Text size="1">{day.weekdayShort}</Text>
                            </Flex>
                        </Box>
                    ))}
                </Flex>
            </Box>

            <Box className="quizzes-list">
                {filteredQuizzes.length > 0 ? (
                    <Flex direction="column" gap="2">
                        {filteredQuizzes.map(renderQuizItem)}
                    </Flex>
                ) : (
                    <Card>
                        <Text size="2" align="center" color="gray">
                            No quizzes for {selectedDay === DateTime.now().weekdayShort ? 'today' : Info.weekdays('long')[Info.weekdays('short').indexOf(selectedDay)]}
                        </Text>
                    </Card>
                )}
            </Box>
            {selectedQuiz && (
                <AlertDialog.Root open={true}>
                    <AlertDialog.Content maxWidth="450px" aria-describedby="alert-dialog-description">
                        <AlertDialog.Title><VisuallyHidden>Quiz</VisuallyHidden></AlertDialog.Title>
                        <AlertDialog.Description><VisuallyHidden>{selectedQuiz.lesson.title}</VisuallyHidden></AlertDialog.Description>
                        <StudentQuiz summary={selectedQuiz} onComplete={handleQuizComplete} setSelectedQuiz={setSelectedQuiz} />
                    </AlertDialog.Content>
                </AlertDialog.Root>
            )}
        </Box>
    );
};

export default StudentFeedBrowser;
